<div class="max-w-6xl mx-auto p-6">
  <!-- Header with Icon -->
  <div class="mb-8 flex items-center gap-4">
    <div
      class="w-12 h-12 bg-gradient-to-r from-orange-400 to-orange-500 rounded-lg flex items-center justify-center"
    >
      <mat-icon class="text-white">person_outline</mat-icon>
    </div>
    <div>
      <h1 class="text-3xl font-bold text-gray-900 mb-0">Profile Information</h1>
      <p class="text-gray-600">Manage your account settings and preferences</p>
    </div>
  </div>

  <!-- Profile Incomplete Alert -->
  <div
    *ngIf="!profileData.isProfileCompleted"
    class="mb-6 bg-gradient-to-r from-orange-50 to-yellow-50 border-l-4 border-orange-400 p-6 rounded-lg shadow-sm"
  >
    <div class="flex items-start">
      <div class="flex-shrink-0">
        <mat-icon class="text-orange-400">warning_outline</mat-icon>
      </div>
      <div class="ml-3 flex-1">
        <h3 class="text-lg font-semibold text-orange-800">
          Complete Your Profile to Access Full Features
        </h3>
        <p class="mt-2 text-sm text-orange-700">
          Your profile is incomplete. Please fill in all required fields to access the full platform
          features.
        </p>
      </div>
    </div>
  </div>

  <!-- Profile Information Card -->
  <div class="bg-white shadow-sm rounded-lg">
    <form [formGroup]="profileForm" (ngSubmit)="handleProfileUpdate()">
      <div class="p-6 border-b border-gray-200">
        <div class="flex items-center justify-between">
          <h2 class="text-xl font-semibold text-gray-900">Profile Information</h2>
          <div *ngIf="!isEditing" class="flex space-x-2">
            <app-common-button
              text="Edit Profile"
              variant="primary"
              (onClick)="startEditing()"
            ></app-common-button>
          </div>
          <div *ngIf="isEditing" class="flex space-x-2">
            <app-common-button
              text="Cancel"
              variant="secondary"
              (onClick)="cancelEdit()"
            ></app-common-button>
            <app-common-button
              text="{{ isLoading ? 'Saving...' : 'Save' }}"
              variant="primary"
              (onClick)="handleProfileUpdate()"
              [disabled]="isLoading || profileForm.invalid"
            ></app-common-button>
          </div>
        </div>
      </div>

      <div class="p-6 space-y-6">
        <!-- Basic Information Section -->
        <div class="border border-gray-200 rounded-lg">
          <div class="p-4 border-b border-gray-200">
            <div class="flex items-center gap-3">
              <div class="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                <mat-icon class="text-orange-600">info_outline</mat-icon>
              </div>
              <h3 class="text-lg font-semibold text-gray-900 mb-0">Basic Information</h3>
            </div>
          </div>
          <div class="p-4 space-y-4">
            <mat-form-field appearance="outline" class="w-full">
              <mat-label>Display Name</mat-label>
              <input
                matInput
                [formControl]="nameControl"
                placeholder="Techky Bro"
                [disabled]="!isEditing"
              />
              <mat-error *ngIf="nameControl.hasError('required') && nameControl.touched">
                Display Name is required
              </mat-error>
              <mat-error *ngIf="nameControl.hasError('minlength') && nameControl.touched">
                Display Name must be at least 2 characters
              </mat-error>
              <mat-error *ngIf="nameControl.hasError('maxlength') && nameControl.touched">
                Display Name must be less than 100 characters
              </mat-error>
            </mat-form-field>

            <mat-form-field appearance="outline" class="w-full">
              <mat-label>Email Address</mat-label>
              <input matInput [formControl]="emailControl" [disabled]="true" />
              <mat-error *ngIf="emailControl.hasError('required') && emailControl.touched">
                Email Address is required
              </mat-error>
              <mat-error *ngIf="emailControl.hasError('email') && emailControl.touched">
                Email Address is invalid
              </mat-error>
            </mat-form-field>
          </div>
        </div>

        <!-- Personal Details Section -->
        <div class="border border-gray-200 rounded-lg">
          <div class="p-4 border-b border-gray-200">
            <div class="flex items-center gap-3">
              <div class="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                <mat-icon class="text-orange-600">person_outline</mat-icon>
              </div>
              <h3 class="text-lg font-semibold text-gray-900 mb-0">Personal Details</h3>
            </div>
          </div>
          <div class="p-4 space-y-4">
            <mat-form-field appearance="outline" class="w-full">
              <mat-label>Individual Name</mat-label>
              <input
                matInput
                [formControl]="individualNameControl"
                placeholder="Enter name"
                [disabled]="!isEditing"
              />
              <mat-error
                *ngIf="individualNameControl.hasError('required') && individualNameControl.touched"
              >
                Individual Name is required
              </mat-error>
              <mat-error
                *ngIf="individualNameControl.hasError('minlength') && individualNameControl.touched"
              >
                Individual Name must be at least 2 characters
              </mat-error>
              <mat-error
                *ngIf="individualNameControl.hasError('maxlength') && individualNameControl.touched"
              >
                Individual Name must be less than 100 characters
              </mat-error>
            </mat-form-field>
          </div>
        </div>

        <!-- Organization Details Section -->
        <div class="border border-gray-200 rounded-lg">
          <div class="p-4 border-b border-gray-200">
            <div class="flex items-center gap-3">
              <div class="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                <mat-icon class="text-orange-600">business</mat-icon>
              </div>
              <h3 class="text-lg font-semibold text-gray-900">Organization Details</h3>
            </div>
          </div>
          <div class="p-4 space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <mat-form-field appearance="outline" class="w-full">
                <mat-label>Organization Name</mat-label>
                <input
                  matInput
                  [formControl]="organisationNameControl"
                  placeholder="Enter organization name"
                  [disabled]="!isEditing"
                />
                <mat-error
                  *ngIf="
                    organisationNameControl.hasError('required') && organisationNameControl.touched
                  "
                >
                  Organization Name is required
                </mat-error>
                <mat-error
                  *ngIf="
                    organisationNameControl.hasError('minlength') && organisationNameControl.touched
                  "
                >
                  Organization Name must be at least 2 characters
                </mat-error>
                <mat-error
                  *ngIf="
                    organisationNameControl.hasError('maxlength') && organisationNameControl.touched
                  "
                >
                  Organization Name must be less than 100 characters
                </mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline" class="w-full">
                <mat-label>Type of Organization</mat-label>
                <mat-select [formControl]="organisationTypeControl" [disabled]="!isEditing">
                  <mat-option value="">Select organization type</mat-option>
                  <mat-option *ngFor="let type of orgTypes" [value]="type.value">
                    {{ type.label }}
                  </mat-option>
                </mat-select>
                <mat-error
                  *ngIf="
                    organisationTypeControl.hasError('required') && organisationTypeControl.touched
                  "
                >
                  Organization Type is required
                </mat-error>
              </mat-form-field>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</div>
